import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, AlertCircle, Star, Building2, ArrowRight } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 8;

/** Stripe Checkout success / polling page. The session_id arrives via query
 *  param. We poll /api/checkout/status until payment_status == 'paid'
 *  (or the webhook beats us to it and entitlement_applied flips true). */
export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ stage: "polling", info: null, attempts: 0 });

  useSeo({
    title: "Confirming payment · Railway Careers UK",
    canonical: "/checkout/success",
    noindex: true,
  });

  useEffect(() => {
    if (!sessionId) {
      setState({ stage: "error", info: { message: "No session ID provided" } });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        const d = r.data;
        if (d.payment_status === "paid" || d.entitlement_applied) {
          setState({ stage: "success", info: d });
          return;
        }
        if (d.status === "expired" || d.payment_status === "failed" || d.payment_status === "unpaid") {
          setState({ stage: "failed", info: d });
          return;
        }
        attempts += 1;
        setState((s) => ({ ...s, info: d, attempts }));
        if (attempts >= MAX_POLLS) {
          setState({ stage: "pending", info: d });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        setState({ stage: "error", info: { message: err.response?.data?.detail || "Status check failed" } });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const isVacancy = state.info?.package_type === "vacancy";
  const PackIcon = isVacancy ? Star : Building2;

  return (
    <div className="bg-cream min-h-screen flex items-center" data-testid="checkout-success-page">
      <div className="max-w-xl mx-auto px-6 py-20 w-full">
        <div className="bg-card border border-border rounded-xl p-8 md:p-10 text-center shadow-sm">
          {state.stage === "polling" && (
            <>
              <Loader2 className="w-10 h-10 text-amber-700 animate-spin mx-auto" />
              <h1 className="font-display font-extrabold text-ink text-3xl tracking-tight mt-5">
                Confirming payment…
              </h1>
              <p className="text-muted-foreground mt-3 text-sm">
                Hang tight — we're verifying your payment with Stripe. This usually takes a few seconds.
              </p>
              {state.attempts > 0 && (
                <p className="text-[11px] text-muted-foreground mt-4 font-mono">Attempt {state.attempts} / {MAX_POLLS}</p>
              )}
            </>
          )}
          {state.stage === "success" && (
            <>
              <div className="w-14 h-14 rounded-full bg-lime-600/15 inline-flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-lime-600" />
              </div>
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-amber-700 font-bold mt-4">
                <PackIcon className="w-3 h-3 fill-amber" /> {isVacancy ? "Featured vacancy active" : "Featured employer active"}
              </div>
              <h1 className="font-display font-extrabold text-ink text-3xl tracking-tight mt-2" data-testid="checkout-success-headline">
                Payment received — thank you!
              </h1>
              <p className="text-muted-foreground mt-3 text-sm">
                {isVacancy
                  ? "Your vacancy is now pinned to the top of every relevant page for the next 30 days, included in the next Monday Briefing, and styled with a gold star across the site."
                  : "Your company is now a Featured Employer for the next 30 days. Your logo is live on the homepage strip, you carry a Featured badge across every page, and your roles get priority placement throughout the platform."}
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild className="bg-navy text-bone hover:bg-lime-600 hover:text-ink">
                  <Link to="/employer-portal/dashboard">Back to dashboard <ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/">View live site</Link>
                </Button>
              </div>
              <div className="mt-6 text-[11px] text-muted-foreground font-mono break-all">
                Receipt ref: {sessionId?.slice(0, 24)}…
              </div>
            </>
          )}
          {state.stage === "pending" && (
            <>
              <Loader2 className="w-10 h-10 text-amber-700 mx-auto" />
              <h1 className="font-display font-extrabold text-ink text-3xl tracking-tight mt-5">
                Payment is processing…
              </h1>
              <p className="text-muted-foreground mt-3 text-sm">
                Stripe is taking longer than usual to confirm. Your features will activate automatically the moment payment is confirmed — usually within a few minutes. You can safely close this page; we'll email you a receipt.
              </p>
              <Button asChild className="mt-7 bg-navy text-bone hover:bg-lime-600 hover:text-ink">
                <Link to="/employer-portal/dashboard">Back to dashboard</Link>
              </Button>
            </>
          )}
          {(state.stage === "failed" || state.stage === "error") && (
            <>
              <div className="w-14 h-14 rounded-full bg-destructive/15 inline-flex items-center justify-center mx-auto">
                <AlertCircle className="w-7 h-7 text-destructive" />
              </div>
              <h1 className="font-display font-extrabold text-ink text-3xl tracking-tight mt-5">
                Payment didn't complete
              </h1>
              <p className="text-muted-foreground mt-3 text-sm">
                {state.info?.message || "The Stripe session didn't complete. No charge has been taken — feel free to try again or contact us."}
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate("/employers#pricing")} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink">
                  Try again
                </Button>
                <Button asChild variant="outline">
                  <a href="mailto:contact@railwaycareers.co.uk">Contact us</a>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
